import { useQuery } from "@tanstack/react-query";
import { FileDown } from "lucide-react";
import { listMyPayouts, myPayoutPdfUrl, type AgentPayout } from "../lib/agent";
import { cn } from "../lib/cn";
import { AdminPreviewBanner, useIsAgent } from "../components/AdminPreviewBanner";
import { getApiToken } from "../lib/api";
import { HelpBox, InfoTip } from "../components/InfoTip";

const STATUS_LABEL: Record<AgentPayout["status"], string> = {
  draft: "In Vorbereitung",
  approved: "Freigegeben",
  sent_to_easybill: "Gutschrift erstellt",
  paid: "Bezahlt",
  failed: "Fehler",
  cancelled: "Storniert",
};

const STATUS_STYLE: Record<AgentPayout["status"], string> = {
  draft: "bg-amber-100 text-amber-900",
  approved: "bg-sky-100 text-sky-900",
  sent_to_easybill: "bg-violet-100 text-violet-900",
  paid: "bg-emerald-100 text-emerald-900",
  failed: "bg-rose-100 text-rose-900",
  cancelled: "bg-slate-200 text-slate-700",
};

function fmtEur(cents: number): string {
  return (cents / 100).toLocaleString("de-DE", { style: "currency", currency: "EUR" });
}

// PDF-Endpoint braucht Auth-Header — wir fetchen, machen Blob-URL, öffnen im neuen Tab
async function openPdfWithAuth(payoutId: string) {
  const url = myPayoutPdfUrl(payoutId);
  const token = getApiToken();
  try {
    const res = await fetch(url, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    if (!res.ok) {
      window.alert(`PDF konnte nicht geladen werden (${res.status})`);
      return;
    }
    const blob = await res.blob();
    const blobUrl = window.URL.createObjectURL(blob);
    window.open(blobUrl, "_blank");
    // GC nach kurzer Zeit
    window.setTimeout(() => window.URL.revokeObjectURL(blobUrl), 30_000);
  } catch (e) {
    window.alert(`PDF-Fehler: ${e instanceof Error ? e.message : "Unbekannt"}`);
  }
}

export function AgentPayouts() {
  const userIsAgent = useIsAgent();
  const { data, isLoading } = useQuery<AgentPayout[]>({
    queryKey: ["agent-payouts"],
    queryFn: () => listMyPayouts(),
    enabled: userIsAgent,
  });

  return (
    <main className="mx-auto w-full max-w-6xl gradient-radial-warm px-4 py-8">
      <div className="text-center mb-8">
        <h2 className="text-2xl md:text-3xl font-semibold tracking-tight">Meine Auszahlungen</h2>
        <p className="mt-2 text-sm text-ink-muted">
          Periodische Abrechnungen deiner freigegebenen Provisionen.
        </p>
      </div>

      {!userIsAgent && <AdminPreviewBanner pageName="Auszahlungen" />}

      {userIsAgent && (
        <div className="mb-4">
          <HelpBox kind="info" collapsible title="💰 Wann bekomme ich mein Geld?">
            Auszahlungen laufen <strong>monatlich</strong>: am Anfang jedes
            Monats werden alle <em>freigegebenen</em> Provisionen der
            Vor-Periode gebündelt, als <em>Gutschrift</em> über easybill
            ausgestellt und auf deine hinterlegte IBAN überwiesen.
            <br />
            <br />
            <strong>Voraussetzung:</strong> Voll-Agent-Status + IBAN +
            (USt-ID oder Kleinunternehmer-Vermerk). Light-Agents sammeln
            ebenfalls Provisionen, brauchen aber das Upgrade fürs Auszahlen.
          </HelpBox>
        </div>
      )}

      {isLoading && <div className="text-sm text-ink-muted">Lade…</div>}

      {data && data.length === 0 && (
        <div className="rounded-2xl border border-dashed border-line bg-white/40 p-12 text-center">
          <p className="text-sm text-ink-muted">
            Noch keine Auszahlungen. Sobald die nächste Abrechnungs-Periode läuft, siehst du sie hier.
          </p>
        </div>
      )}

      <div className="space-y-3">
        {data?.map((p) => (
          <div key={p.id} className="rounded-2xl border border-line bg-white p-4">
            <div className="flex items-start justify-between gap-3">
              <div>
                <div className="font-semibold">
                  {new Date(p.period_start).toLocaleDateString("de-DE")} –{" "}
                  {new Date(p.period_end).toLocaleDateString("de-DE")}
                </div>
                <div className="mt-1 text-xs text-ink-muted">
                  Erstellt am {new Date(p.created_at).toLocaleDateString("de-DE")}
                </div>
              </div>
              <span
                className={cn(
                  "inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium",
                  STATUS_STYLE[p.status],
                )}
              >
                {STATUS_LABEL[p.status]}
              </span>
            </div>

            <div className="mt-3 grid grid-cols-3 gap-3 text-sm">
              <div>
                <div className="text-xs text-ink-muted">Provisionen</div>
                <div className="font-mono">{fmtEur(p.commissions_amount_cents)}</div>
              </div>
              {p.clawbacks_amount_cents > 0 && (
                <div>
                  <div className="text-xs text-ink-muted flex items-center gap-1">
                    Verrechnung
                    <InfoTip title="Was ist Verrechnung?" maxWidth={240}>
                      Bereits ausgezahlte Provisionen aus früheren Perioden
                      die nachträglich storniert wurden (Kunde hat
                      widerrufen, Rückerstattung) — werden hier verrechnet.
                    </InfoTip>
                  </div>
                  <div className="font-mono text-rose-700">
                    −{fmtEur(p.clawbacks_amount_cents)}
                  </div>
                </div>
              )}
              <div>
                <div className="text-xs text-ink-muted">Auszahlung</div>
                <div className="font-mono font-semibold text-base">
                  {fmtEur(p.net_amount_cents)}
                </div>
              </div>
            </div>

            {p.easybill_credit_note_id && (
              <div className="mt-3 flex items-center gap-3">
                <div className="text-xs text-ink-muted font-mono">
                  Gutschrift-Nr.: #{p.easybill_credit_note_id}
                </div>
                <button
                  onClick={() => openPdfWithAuth(p.id)}
                  className="inline-flex items-center gap-1 text-xs text-accent-orange hover:underline"
                >
                  <FileDown className="size-3" />
                  PDF
                </button>
              </div>
            )}
            {p.paid_at && (
              <div className="mt-1 text-xs text-emerald-700">
                Ausgezahlt am {new Date(p.paid_at).toLocaleDateString("de-DE")}
                {p.payment_reference && (
                  <span className="ml-2 font-mono">· Ref: {p.payment_reference}</span>
                )}
              </div>
            )}
          </div>
        ))}
      </div>
    </main>
  );
}
